/**
 * Who is actually talking.
 *
 * A meeting used to highlight the local tile whenever the microphone was
 * *unmuted*, which is a different question — it stayed lit through an hour of
 * silence and told nobody anything. This measures waveform energy instead, so
 * the highlight follows the voice.
 *
 * Two details keep it from flickering: a speech threshold that is higher to
 * cross than to fall below, and a hold time that keeps a tile lit through the
 * natural gaps between words.
 */

const SPEAKING_ON = 0.045;
/** Lower than `SPEAKING_ON`, so ordinary breath noise cannot toggle the ring. */
const SPEAKING_OFF = 0.03;
/** Keeps a tile lit across pauses between words rather than strobing. */
const HOLD_MS = 700;
const POLL_MS = 200;

interface Monitored {
  source: MediaStreamAudioSourceNode;
  analyser: AnalyserNode;
  samples: Uint8Array<ArrayBuffer>;
  /**
   * Chrome historically delivers silence to a Web Audio graph reading a remote
   * WebRTC stream unless that stream is also attached to a media element. The
   * element is muted and never inserted into the document; it exists purely to
   * make the browser pump the stream.
   */
  sink: HTMLAudioElement | null;
  speaking: boolean;
  lastLoudAt: number;
}

function rms(analyser: AnalyserNode, samples: Uint8Array<ArrayBuffer>): number {
  analyser.getByteTimeDomainData(samples);
  let total = 0;
  for (const sample of samples) {
    const normalized = (sample - 128) / 128;
    total += normalized * normalized;
  }
  return Math.sqrt(total / samples.length);
}

export class SpeakingMonitor {
  private context: AudioContext | null = null;
  private readonly monitored = new Map<string, Monitored>();
  private timer: number | null = null;
  private lastEmitted = '';

  constructor(private readonly onChange: (speaking: Set<string>) => void) {}

  private ensureContext(): AudioContext {
    if (!this.context) {
      const AudioContextClass =
        window.AudioContext ??
        (window as typeof window & { webkitAudioContext?: typeof AudioContext })
          .webkitAudioContext;
      this.context = new AudioContextClass();
    }
    // A context created before the first user gesture starts suspended, and a
    // suspended analyser reads a flat zero — which would look exactly like a
    // room where nobody is talking.
    if (this.context.state === 'suspended') void this.context.resume().catch(() => undefined);
    return this.context;
  }

  /** Starts watching a stream, replacing any previous one under this id. */
  track(id: string, stream: MediaStream, { remote }: { remote: boolean }): void {
    if (!stream.getAudioTracks().length) return;
    this.untrack(id);

    let context: AudioContext;
    try {
      context = this.ensureContext();
    } catch {
      // No Web Audio (or blocked by policy) — the meeting works without it.
      return;
    }

    const source = context.createMediaStreamSource(stream);
    const analyser = context.createAnalyser();
    analyser.fftSize = 256;
    analyser.smoothingTimeConstant = 0.4;
    source.connect(analyser);

    let sink: HTMLAudioElement | null = null;
    if (remote) {
      sink = new Audio();
      sink.srcObject = stream;
      sink.muted = true;
      void sink.play().catch(() => undefined);
    }

    this.monitored.set(id, {
      source,
      analyser,
      samples: new Uint8Array(new ArrayBuffer(analyser.fftSize)),
      sink,
      speaking: false,
      lastLoudAt: 0,
    });
    this.start();
  }

  untrack(id: string): void {
    const entry = this.monitored.get(id);
    if (!entry) return;
    this.monitored.delete(id);
    try {
      entry.source.disconnect();
      entry.analyser.disconnect();
    } catch {
      // The context may already be closed.
    }
    if (entry.sink) {
      entry.sink.pause();
      entry.sink.srcObject = null;
    }
    if (!this.monitored.size) this.stopTimer();
  }

  /** Ignores a muted participant, so a muted tile cannot appear to be talking. */
  setMuted(id: string, muted: boolean): void {
    const entry = this.monitored.get(id);
    if (!entry || !muted) return;
    entry.speaking = false;
    entry.lastLoudAt = 0;
  }

  private start(): void {
    if (this.timer !== null) return;
    this.timer = window.setInterval(() => this.sample(), POLL_MS);
  }

  private stopTimer(): void {
    if (this.timer === null) return;
    window.clearInterval(this.timer);
    this.timer = null;
  }

  private sample(): void {
    const now = Date.now();
    const speaking = new Set<string>();

    for (const [id, entry] of this.monitored) {
      const level = rms(entry.analyser, entry.samples);
      if (level >= SPEAKING_ON) {
        entry.speaking = true;
        entry.lastLoudAt = now;
      } else if (entry.speaking && level < SPEAKING_OFF && now - entry.lastLoudAt > HOLD_MS) {
        entry.speaking = false;
      }
      if (entry.speaking) speaking.add(id);
    }

    // Re-rendering every tile five times a second would be worse than the
    // problem this solves, so only a change in the set is published.
    const signature = [...speaking].sort().join('|');
    if (signature === this.lastEmitted) return;
    this.lastEmitted = signature;
    this.onChange(speaking);
  }

  destroy(): void {
    this.stopTimer();
    for (const id of [...this.monitored.keys()]) this.untrack(id);
    void this.context?.close().catch(() => undefined);
    this.context = null;
    this.lastEmitted = '';
  }
}
